import type * as PIXI from 'pixi.js';

import { CreationType, DynamicStylingCreationLevel, ValueMapMode } from '../enums';
import type { AssetTemplateReferenceMap, DestroyOptions } from '../types';
import { DynamicAnimationConfiguration } from '../libs/DynamicAnimationConfiguration';
import { DynamicGraphicsConfigurator } from '../libs/DynamicGraphicsConfigurator';
import { holdConfigCurveAtRest } from '../libs/AnimationCurveManager';
import { assertInEnum } from '../validators';
import type { LoadSettings } from './serializableMixin';
import {
    getTemplateValueAttribute,
    isTemplateReference,
    objectHasTemplateValues,
    stripTemplateReferencePrefix,
} from './attributes/template/common';
import { getAllTemplateAttributes } from './attributes';
import { applyGradientFillColors } from './colorGraphicsMap';
import type { GradientFillGraphic } from './colorGraphicsMap';
import type { BaseAsset } from './asset/BaseAsset';
import { MorphManager } from './morph/MorphManager';

export interface AnimatableContainer {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    alpha: number;
    scale: PIXI.ObservablePoint;
    pivot: PIXI.ObservablePoint;
    mask: PIXI.Container | PIXI.MaskData | null;
    getBounds(skipUpdate?: boolean, rect?: PIXI.Rectangle): PIXI.Rectangle;
}

export type DynamicStyleData = {
    objectAnimation: Record<string, unknown>[];
    colorGraphics: Record<string, unknown>[];
    alphaGraphics: Record<string, unknown>[];
};

export type DynamicStylingMixinInterface = {
    objectAnimation: DynamicAnimationConfiguration[];
    colorGraphics: DynamicGraphicsConfigurator[];
    alphaGraphics: DynamicGraphicsConfigurator[];
    get hasDynamicStyle(): boolean;
    hasColorGraphic: boolean;
    hasObjectAnimation: boolean;
    hasAlphaGraphic: boolean;
    getCreationContainersAtLevelForType(
        level: DynamicStylingCreationLevel,
        type?: CreationType,
    ): AnimatableContainer[];
    addObjectAnimation(): DynamicAnimationConfiguration;
    removeObjectAnimation(index: number): void;
    _prepareDynamicStylingForUpdate(): void;
    _updateDynamicStyling(): void;
    _updateObjectAnimation(): void;
    addColorGraphic(settings?: Record<string, unknown>): DynamicGraphicsConfigurator;
    removeColorGraphic(index: number): void;
    addAlphaGraphic(settings?: Record<string, unknown>): DynamicGraphicsConfigurator;
    removeAlphaGraphic(index: number): void;
    saveDynamicStyle(): DynamicStyleData;
    loadDynamicStyle(settings: DynamicStyleData): void;
    destroyDynamicStyles(): void;
    buildColorGraphicsMap(): Record<string, number>;
};

type DynamicStylingMixinResult = new (
    ...args: ConstructorParameters<typeof BaseAsset>
) => BaseAsset & DynamicStylingMixinInterface;

/**
 * This mixin adds support for dynamic styling. Classes that use the mixin must inherit from BaseAsset.
 *
 * Classes using this Mixin may override `getCreationContainersAtLevelForType()` to return applicable containers.
 */
export const DynamicStylingMixin = (Base: typeof BaseAsset): DynamicStylingMixinResult => {
    return class DynamicStylingMixin extends Base implements AnimatableContainer {
        override _classType = 'DynamicStylingMixin';

        /**
         * A list of animations which operate on the entire asset object. For text assets specifically these animations
         * affect the entire block of text, not specific lines or words.
         */
        objectAnimation: DynamicAnimationConfiguration[] = [];

        colorGraphics: DynamicGraphicsConfigurator[] = [];
        alphaGraphics: DynamicGraphicsConfigurator[] = [];

        /**
         * Returns true if the asset has any configurators which make up a dynamic style. This can be an object
         * animation, color or alpha graphic configuration.
         */
        get hasDynamicStyle(): boolean {
            return this.hasObjectAnimation || this.hasAlphaGraphic || this.hasColorGraphic;
        }

        /** Returns true if any of its styles color or fontUrl properties are driven by the global template */
        override _hasTemplateReferenceValues(): boolean {
            if (this.colorGraphics.some((graphic) => objectHasTemplateValues(graphic.shape))) {
                return true;
            }
            if (this.alphaGraphics.some((graphic) => objectHasTemplateValues(graphic.shape))) {
                return true;
            }
            return super._hasTemplateReferenceValues();
        }

        /**
         * True if the asset contains any color graphic configurators
         */
        get hasColorGraphic(): boolean {
            return this.colorGraphics.length > 0;
        }

        /**
         * Returns True if the asset has any `DynamicAnimationConfiguration`s.
         */
        get hasObjectAnimation(): boolean {
            return this.objectAnimation.length > 0;
        }

        /**
         * True if the asset contains any alpha graphic configurators
         */
        get hasAlphaGraphic(): boolean {
            return this.alphaGraphics.length > 0;
        }

        /**
         * Returns the creation containers for the given level used for dynamic styling. Currently there are 3 different
         * levels: object, line and word. It is possible for an asset to only support one level. In that case, an empty
         * array will be returned for the unsupported level.
         */
        getCreationContainersAtLevelForType(
            level: DynamicStylingCreationLevel,
            type = CreationType.Animation,
        ): AnimatableContainer[] {
            assertInEnum(type, CreationType);
            let result = [];
            switch (level) {
                case DynamicStylingCreationLevel.Object:
                    result = [this];
                    break;
                default:
                    throw new Error(`Unsupported container of type ${level}`);
            }
            return result;
        }

        /**
         * This prepares dynamic styles for update by removing any existing assets from the
         * container hierarchy. In order to complete the dynamic style update, _updateDynamicStyling
         * should be called after this. Any calculations which should happen independent of dynamic
         * styles should happen in between these two steps.
         */
        _prepareDynamicStylingForUpdate() {
            this.alphaGraphics.forEach((configurator) => configurator.prepareForUpdate());
            this.colorGraphics.forEach((configurator) => configurator.prepareForUpdate());
        }

        _updateDynamicStyling() {
            if (this.hasMorphAnimation || this.hasMorphLeadInAnimation) {
                this._updateMorphAnimation();
            }

            this._updateObjectAnimation();
            this.alphaGraphics.forEach((configurator) => configurator.collectProperties());
            this.colorGraphics.forEach((configurator) => configurator.collectProperties());
            this.alphaGraphics.forEach((configurator) => configurator.createOrUpdateGraphics());
            this.colorGraphics.forEach((configurator) => configurator.createOrUpdateGraphics());
        }

        /**
         * Adds a `DynamicAnimationConfiguration` to the `objectAnimation` list. The added configurator can be configured to
         * animate any attribute on the asset. Check `DynamicAnimationConfiguration` for details.
         */
        addObjectAnimation(): DynamicAnimationConfiguration {
            const animationConfigurator = new DynamicAnimationConfiguration({
                parent: this,
            });
            // do not mutate, we need to create a new reference for React
            this.objectAnimation = [...this.objectAnimation, animationConfigurator];
            this.requiresRedraw = true;
            return animationConfigurator;
        }

        /**
         * Remove the DynamicAnimationConfiguration at the given index
         */
        removeObjectAnimation(index: number) {
            this.objectAnimation.splice(index, 1);
            this.objectAnimation = [...this.objectAnimation];
            this.requiresRedraw = true;
        }

        _updateObjectAnimation() {
            const morphedProperties = this.morphAnimation.map((animation) => animation.property);
            this.objectAnimation.forEach((configurator) => {
                if (!configurator.enabled) {
                    return;
                }

                const conflictsWithMorph = morphedProperties.includes(configurator.property);
                if (conflictsWithMorph) {
                    return;
                }
                configurator.anchorsToRemove = this._animationAnchorsToRemove;
                const animationCurveConfig = configurator.buildConfig();
                if (this.isMatchCutFrozen) {
                    holdConfigCurveAtRest(animationCurveConfig);
                } else {
                    MorphManager.modifyNonMorphCurve(
                        animationCurveConfig,
                        this.hasMorphLeadInAnimation,
                        this.hasMorphAnimation,
                    );
                }

                this.animationManager.add(animationCurveConfig);

                if (configurator.scaleAnchor === 'center') {
                    const isScaleX = configurator.property === 'scale.x';
                    const isScaleY = configurator.property === 'scale.y';
                    if (isScaleX || isScaleY) {
                        configurator._getLevelObjects().forEach((obj) => {
                            if (isScaleX) {
                                const hw = obj.width / 2 / obj.scale.x;
                                // Skip if already centered on this axis (e.g. MediaAsset containers)
                                if (Math.abs(obj.pivot.x - hw) >= 1) {
                                    obj.x += hw * obj.scale.x;
                                    obj.pivot.x = hw;
                                }
                            }
                            if (isScaleY) {
                                const hh = obj.height / 2 / obj.scale.y;
                                if (Math.abs(obj.pivot.y - hh) >= 1) {
                                    obj.y += hh * obj.scale.y;
                                    obj.pivot.y = hh;
                                }
                            }
                        });
                    }
                }
            });
        }

        _destroyObjectAnimation() {
            this.objectAnimation = [];
        }

        _objectAnimationCustomLoad(properties: Record<string, unknown>[]) {
            if (!properties) {
                return;
            }
            this.objectAnimation.splice(properties.length);
            properties.forEach((dataObj, i) => {
                const configurator = this.objectAnimation[i] ?? this.addObjectAnimation();
                configurator.load(dataObj);
            });
        }

        /**
         * Adds a color graphic configurator
         * @param settings Additional settings to pass through to the configurator.
         */
        addColorGraphic(settings = {}): DynamicGraphicsConfigurator {
            const configurator = new DynamicGraphicsConfigurator({
                ...settings,
                parent: this,
            });
            // do not mutate, we need to create a new reference for React
            this.colorGraphics = [...this.colorGraphics, configurator];
            this.requiresRedraw = true;
            return configurator;
        }

        /**
         * Removes the color graphic configurator with the given index
         */
        removeColorGraphic(index: number) {
            this.colorGraphics[index].destroy();
            this.colorGraphics.splice(index, 1);
            this.colorGraphics = [...this.colorGraphics];
            this.requiresRedraw = true;
        }

        _destroyColorGraphics() {
            this.colorGraphics.forEach((graphic) => graphic.destroy());
            this.colorGraphics = [];
        }

        _colorGraphicsCustomLoad(
            properties: Record<string, unknown>[],
            settings = { skipTemplateAttributes: false },
        ) {
            if (!properties) {
                return;
            }
            for (let i = properties.length; i < this.colorGraphics.length; i++) {
                this.colorGraphics[i].destroy();
            }
            this.colorGraphics.splice(properties.length);

            properties.forEach((dataObj, index) => {
                const configurator = this.colorGraphics[index] ?? this.addColorGraphic();
                configurator.load(dataObj, settings);
            });
        }

        /**
         * Adds a alpha graphic configurator
         * @param settings Additional settings to pass through to the configurator.
         */
        addAlphaGraphic(settings = {}): DynamicGraphicsConfigurator {
            const configurator = new DynamicGraphicsConfigurator({
                ...settings,
                parent: this,
            });
            // do not mutate, we need to create a new reference for React
            this.alphaGraphics = [...this.alphaGraphics, configurator];
            this.requiresRedraw = true;
            return configurator;
        }

        /**
         * Removes the alpha graphic configurator with the given index
         */
        removeAlphaGraphic(index: number) {
            this.alphaGraphics[index].destroy();
            this.alphaGraphics.splice(index, 1);
            this.alphaGraphics = [...this.alphaGraphics];
            this.requiresRedraw = true;
        }

        _destroyAlphaGraphics() {
            this.alphaGraphics.forEach((graphic) => graphic.destroy());
            this.alphaGraphics = [];
        }

        _alphaGraphicsCustomLoad(properties: Record<string, unknown>[], settings: LoadSettings) {
            if (!properties) {
                return;
            }
            for (let i = properties.length; i < this.alphaGraphics.length; i++) {
                this.alphaGraphics[i].destroy();
            }
            this.alphaGraphics.splice(properties.length);

            properties.forEach((dataObj, index) => {
                const configurator = this.alphaGraphics[index] ?? this.addAlphaGraphic();
                configurator.load(dataObj, settings);
            });
        }

        override _setAutoDrawForAllChildren(value: boolean) {
            super._setAutoDrawForAllChildren(value);

            this.objectAnimation.forEach((configurator) => {
                configurator.autoUpdate = value;
            });
            this.alphaGraphics.forEach((configurator) => {
                configurator.autoUpdate = value;
            });
            this.colorGraphics.forEach((configurator) => {
                configurator.autoUpdate = value;
            });
        }

        override get propertiesToSerialize() {
            return super.propertiesToSerialize.concat([
                'objectAnimation',
                'colorGraphics',
                'alphaGraphics',
            ]);
        }

        /**
         * Serializes the dynamic style configuration (objectAnimation, colorGraphics, alphaGraphics)
         * into a plain data object that can be stored and later restored with loadDynamicStyle.
         */
        saveDynamicStyle(): DynamicStyleData {
            const saved = this._savePropertyList([
                'colorGraphics',
                'alphaGraphics',
                'objectAnimation',
            ]);
            return {
                objectAnimation: saved.objectAnimation as Record<string, unknown>[],
                colorGraphics: saved.colorGraphics as Record<string, unknown>[],
                alphaGraphics: saved.alphaGraphics as Record<string, unknown>[],
            };
        }

        /**
         * Loads a dynamic style configuration previously saved with saveDynamicStyle.
         */
        loadDynamicStyle(settings: DynamicStyleData) {
            this._objectAnimationCustomLoad(settings.objectAnimation);
            this._alphaGraphicsCustomLoad(settings.alphaGraphics, {});
            this._colorGraphicsCustomLoad(settings.colorGraphics);
        }

        destroyDynamicStyles() {
            this._destroyObjectAnimation();
            this._destroyAlphaGraphics();
            this._destroyColorGraphics();
        }

        override destroy(options?: DestroyOptions) {
            super.destroy(options);
            this.destroyDynamicStyles();
        }

        override buildTemplateReferenceMap(): AssetTemplateReferenceMap {
            return {
                ...super.buildTemplateReferenceMap(),
                ...(this.#buildColorGraphicsValueMap(
                    ValueMapMode.GET_REFERENCE_VALUES,
                    false,
                ) as Record<string, string>),
            };
        }

        /**
         * Get all colorGraphics attributes and return only the visible color related attributes to be exposed for user manual color changes.
         * We return an object where the attribute is the key and the reference value is the value.
         */
        buildColorGraphicsMap(): Record<string, number> {
            const colorMap: Record<string, number> = {};
            const valueMap = this.#buildColorGraphicsValueMap(
                ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE,
                true,
            ) as Record<string, number>;
            Object.entries(valueMap).map(([valueKey, value]) => {
                if (
                    valueKey.toLowerCase().includes('color') &&
                    valueKey.toLowerCase().includes('alpha') === false &&
                    valueKey.toLowerCase().includes('gradient') === false
                ) {
                    colorMap[valueKey] = value;
                }
            });
            // For gradient fills, expose the start/end stop colours instead of the solid fill colour.
            this.colorGraphics.forEach((graphic, index) => {
                applyGradientFillColors(
                    colorMap,
                    graphic.shape as unknown as GradientFillGraphic,
                    `colorGraphics.${index}.shape`,
                    'fillColor',
                );
            });
            return colorMap;
        }

        /**
         * Get all colorGraphics color related attributes.
         * We return an object where the attribute is the key and the reference value is the value.
         * @param valueMapMode
         * @param onlyVisible - this boolean will decide (for the time being) if it should return a strokeColor value when
         * there is a visible stroke
         */
        #buildColorGraphicsValueMap(
            valueMapMode = ValueMapMode.GET_REFERENCE_VALUES,
            onlyVisible = false,
        ): Record<string, string | number> {
            const referenceMap: Record<string, string | number> = {};
            // get dynamic styling properties
            this.colorGraphics.forEach((graphic, index) => {
                const attrKeyPrefix = `colorGraphics.${index}.shape`;
                const templateAttributes = getAllTemplateAttributes(graphic.shape, valueMapMode);
                templateAttributes.forEach((attr) => {
                    if (
                        onlyVisible &&
                        attr === 'strokeColor' &&
                        graphic.shape['strokeWidth'] === 0
                    ) {
                        return;
                    }
                    const attrKey = `${attrKeyPrefix}.${attr}`;
                    const value =
                        valueMapMode === ValueMapMode.GET_REFERENCE_VALUES
                            ? getTemplateValueAttribute(graphic.shape, attr)
                            : graphic.shape[attr];

                    if (isTemplateReference(value)) {
                        referenceMap[attrKey] = stripTemplateReferencePrefix(value);
                    } else {
                        referenceMap[attrKey] = value;
                    }
                });
            });
            return referenceMap;
        }

        override _maskCustomSave(): object | null {
            if (this.hasAlphaGraphic) {
                // the alpha graphic is managing the mask so we shouldn't also save a static
                // config for it (since dynamic styles creates it)
                return null;
            }
            return super._maskCustomSave();
        }
    };
};
